import {
  IQuickVideoForm,
  IQuickVideoPartOption,
  IQuickVideoPartPositionOption,
  IVideoDetails,
  QuickVideoPartType,
} from 'lib/api/quickVideo/types';
import {
  FormikDropdownField,
  FormikDropdownOption,
} from 'lib/components/formik/FormikDropdown';
import { Gap } from 'lib/components/styles/layout';
import React, { useState } from 'react';
import { Button } from 'react-covideo-common';
import { HiPlus } from 'react-icons/hi';
import { useFormikContext } from 'formik';
import { AddVideoModal } from '../modals/AddVideoModal';
import { SelectedVideoCardSmall } from './SelectedVideoCardSmall';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { useTheme } from 'styled-components/macro';

type VideoPositionDropdownProps = {
  quickVideoPart: IQuickVideoPartPositionOption;
  options: IQuickVideoPartOption[];
  index: number;
  replace: <X = any>(index: number, value: X) => void;
};

export const VideoPositionComponent = ({
  quickVideoPart,
  options,
  index,
  replace,
}: VideoPositionDropdownProps) => {
  const [openAddDealershipVideoModal, setOpenAddDealershipVideoModal] =
    useState(false);
  const { values } = useFormikContext<IQuickVideoForm>();
  const theme = useTheme();

  const chosenFieldValues = values.quickVideoParts[index];
  const isDealershipVideo =
    chosenFieldValues?.partType === QuickVideoPartType.DEALERSHIP_VIDEO;
  const isVideoTypeSelected = !!chosenFieldValues?.partType;
  const isDealershipVideoSelected =
    isDealershipVideo && !!chosenFieldValues?.video;

  const handleQuickVideoPartChange = (
    option: FormikDropdownOption | null | undefined
  ) => {
    replace(index, {
      ...chosenFieldValues,
      partPosition: quickVideoPart.value,
      partType: option ? option.value : '',
      description: option ? chosenFieldValues.description : '', // reset description if option is cleared
      video: null,
    });
  };

  const onOpenAddVideoModal = () => {
    setOpenAddDealershipVideoModal(true);
  };

  const onCloseAddVideoModal = () => {
    setOpenAddDealershipVideoModal(false);
  };

  return (
    <>
      <Gap
        flexDirection='column'
        width='100%'
        gap='18px'
        key={quickVideoPart.value}
      >
        <FormikDropdownField
          name={`quickVideoParts[${index}].partType`}
          options={options}
          label={`${quickVideoPart.label} Type`}
          hideError
          isRequired={!!quickVideoPart.required}
          isClearable
          placeholder={`Choose ${quickVideoPart.label.toLocaleLowerCase()}`}
          onChange={handleQuickVideoPartChange}
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[80],
            },
          }}
        />

        <FormikTextAreaField
          name={`quickVideoParts[${index}].description`}
          label={`${quickVideoPart.label} Description / Instructions`}
          isRequired={false}
          disabled={!isVideoTypeSelected}
          maxLength={150}
          showCounter
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[80],
            },
            textAreaStyles: {
              resize: 'none',
              height: '62px',
              minHeight: 'unset',
            },
          }}
        />
        {isDealershipVideoSelected ? (
          <SelectedVideoCardSmall
            video={chosenFieldValues?.video || ({} as IVideoDetails)}
            onClear={() => {
              replace(index, {
                ...chosenFieldValues,
                video: null,
              });
            }}
          />
        ) : (
          <Button
            variant='secondary'
            onClick={onOpenAddVideoModal}
            disabled={!isDealershipVideo || !isVideoTypeSelected}
            text='Add Video'
            icon={<HiPlus />}
            style={{
              width: '100%',
              height: '77px',
              maxHeight: 'unset',
            }}
          />
        )}
      </Gap>
      {openAddDealershipVideoModal && (
        <AddVideoModal
          type={chosenFieldValues?.partType}
          onClose={onCloseAddVideoModal}
          onAddVideo={(video: IVideoDetails) => {
            replace(index, {
              ...chosenFieldValues,
              video,
            });
          }}
        />
      )}
    </>
  );
};
